import _ from "lodash";
import { IClientConfig } from "src/client-config/config";
import { BetTypeEnum } from "src/constants/enums/betType";
import { BonusRuleBasisEnum } from "src/constants/enums/bonus";
import { IBonusRule, ICurrencyConfig, IPartnerConfig } from "src/constants/interfaces";
import { ISelection } from "src/constants/interfaces/bet";
import { getTotalOdd } from "./odd";
import { getTaxedStake } from "./tax";
import { getPrice } from "./priceCalculation";

const isPassingBonusRule = (rule: IBonusRule, selections: ISelection[], totalOdd: number, stake: number, partnerConfig?: IPartnerConfig) => {
  const actualSelections = rule.IgnoreLowOddSelection
    ? selections.filter(item => getPrice(item) > rule.MinOdds)
    : selections;

  const isOddsCorrect = actualSelections.every(item => getPrice(item) >= rule.MinOdds);

  const isSelectionsCountCorrect = rule.MaximumSelections
    ? _.inRange(actualSelections.length, rule.MinimumSelections, rule.MaximumSelections + 1)
    : actualSelections.length >= rule.MinimumSelections;

  const bonusRuleByCurrency = rule?.MinBetStakes?.MinStakes?.find((item) => item.Currency === partnerConfig?.currency);

  const isStakeCorrect = !bonusRuleByCurrency || stake >= bonusRuleByCurrency.Amount;

  return isOddsCorrect && isSelectionsCountCorrect && totalOdd >= rule.MinBetOdds && isStakeCorrect;
};

const getActiveBonusRules = (
  config: IClientConfig,
  bonusRules: IBonusRule[],
  selections: ISelection[],
  stake: number,
  partnerConfig?: IPartnerConfig,
  ignoreConfig = false,

) => {
  if (!config.enableExpressBonus && !ignoreConfig) return [];

  const totalOdd = getTotalOdd(config, partnerConfig, BetTypeEnum.Multiple, selections) || 0;

  const passedRules = [...bonusRules]
    .sort((a, b) => b.AmountPercent - a.AmountPercent)
    .filter(rule => isPassingBonusRule(rule, selections, totalOdd, stake, partnerConfig));

  return passedRules;
};

export const getExpressBonus = (
  config: IClientConfig,
  stake: number,
  selections: ISelection[],
  bonusRules?: IBonusRule[],
  partnerConfig?: IPartnerConfig,
  currencyConfig?: ICurrencyConfig,
) => {
  const noExpressBonus = { exactLosing: 0, amount: 0, percent: 0 };

  if (!selections?.length || !bonusRules) return noExpressBonus;

  const allPassedRules = getActiveBonusRules(config, bonusRules, selections, stake, partnerConfig);
  const hasExactLosing = +allPassedRules.some(item => item.ExactLosing);
  const activeRule = allPassedRules.find(item => !item.ExactLosing && item.Basis);

  if (!activeRule) return { ...noExpressBonus, exactLosing: hasExactLosing };

  const totalOdd = getTotalOdd(config, partnerConfig, BetTypeEnum.Multiple, selections) || 0;
  const taxedStake = getTaxedStake(partnerConfig, stake, currencyConfig);
  const profit = calcProfitByBasis[activeRule.Basis](taxedStake, totalOdd) || 0;

  const { MinBetStakes, AmountPercent } = activeRule;
  const maxBonusAmount = MinBetStakes?.MinStakes?.[0]?.MaxBonusAmount;

  const calculatedBonusAmount = (profit * AmountPercent) / 100;

  const bonusAmount = maxBonusAmount
    ? Math.min(calculatedBonusAmount, maxBonusAmount)
    : calculatedBonusAmount;

  return {
    isMax: !!maxBonusAmount && calculatedBonusAmount > maxBonusAmount,
    percent: activeRule.AmountPercent,
    exactLosing: hasExactLosing,
    amount: bonusAmount,
  };
};

const calcProfitByBasis = {
  [BonusRuleBasisEnum.Default]: (stake: number) => stake,
  [BonusRuleBasisEnum.Profit]: (stake: number, totalOdd: number) => stake * (totalOdd - 1),
  [BonusRuleBasisEnum.Return]: (stake: number, totalOdd: number) => stake * totalOdd,
};
